import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogInIcon, MailIcon, ShieldCheckIcon, ArrowLeftIcon } from 'lucide-react';
import { LoadingScreen } from '../components/LoadingScreen';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../config/theme';
const LOGIN_IMAGES = ['https://cdn.magicpatterns.com/uploads/8Tni1q1GBnHeKwTh5tNMwY/rabbit_1.jpg', 'https://cdn.magicpatterns.com/uploads/brjGpNxkg6WrCpMb1PvBpa/rabbit_2.jpg'];
// Destructure for cleaner access
const {
  brand,
  colors
} = theme;
export function Login() {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [step, setStep] = useState<'login' | 'mfa' | 'loading'>('login');
  const [email, setEmail] = useState('simon.verraest@siamtrading.th');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [heroImage] = useState(() => LOGIN_IMAGES[Math.floor(Math.random() * LOGIN_IMAGES.length)]);
  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);
  // Auto-focus first MFA input when entering MFA step
  useEffect(() => {
    if (step === 'mfa') {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);
  const getMaskedEmail = () => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const masked = local.charAt(0) + '•••' + (local.length > 1 ? local.charAt(local.length - 1) : '');
    return `${masked}@${domain}`;
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('mfa');
    setResendCountdown(30);
  };
  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('loading');
    setTimeout(() => {
      login();
      navigate('/overview');
    }, 2000);
  };
  const handleMfaInput = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...mfaCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) newCode[index + i] = digit;
      });
      setMfaCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleMfaKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newCode = [...mfaCode];
      newCode[index - 1] = '';
      setMfaCode(newCode);
    }
  };
  const handleResend = () => {
    if (resendCountdown > 0) return;
    setMfaCode(['', '', '', '', '', '']);
    setResendCountdown(30);
    inputRefs.current[0]?.focus();
  };
  const isCodeComplete = mfaCode.every(digit => digit !== '');
  if (step === 'loading') {
    return <LoadingScreen data-id="element-9148" />;
  }
  return <div className="min-h-screen flex bg-white" data-id="element-9149">
      {/* Left Side - Image & Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative" style={{
      backgroundColor: colors.background.hero
    }} data-id="element-9150">
        <img src={heroImage} alt="Thailand" className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-9151" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" data-id="element-9152" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full" data-id="element-9153">
          <div data-id="element-9154">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-16 w-auto object-contain bg-[#0F1B2D] px-4 py-3 rounded-lg" data-id="element-9155" />
          </div>
          <div className="max-w-xl" data-id="element-9156">
            <h1 className="text-4xl font-bold mb-4 leading-tight" style={{
            color: colors.text.inverse
          }} data-id="element-9157">
              Powering Thailand's Digital Commerce
            </h1>
            <p className="text-lg text-gray-200" data-id="element-9158">
              The complete payment solution for modern merchants. Manage
              transactions, track settlements, and grow your business with
              confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form / MFA */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center px-6 py-12 sm:px-12" data-id="element-9159">
        <div className="w-full" style={{
        maxWidth: theme.layout.auth.maxWidth
      }} data-id="element-9160">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8" data-id="element-9161">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-10 w-auto object-contain" data-id="element-9162" />
          </div>

          {step === 'login' && <>
              <div className="mb-10 text-center lg:text-left" data-id="element-9163">
                <h2 className="text-3xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-9164">
                  Merchant Portal
                </h2>
                <p style={{
              color: colors.text.secondary
            }} data-id="element-9165">
                  Sign in to manage your business payments
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6" data-id="element-9166">
                <div data-id="element-9167">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-9168">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="simon.verraest@siamtrading.th" className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={{
                border: `1px solid ${colors.border.DEFAULT}`,
                backgroundColor: colors.neutral[50]
              }} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-9169" />
                </div>

                <div data-id="element-9170">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-9171">
                    Password
                  </label>
                  <input type="password" defaultValue="••••••••" placeholder="••••••••" className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={{
                border: `1px solid ${colors.border.DEFAULT}`,
                backgroundColor: colors.neutral[50]
              }} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-9172" />
                </div>

                <div className="flex items-center justify-between" data-id="element-9173">
                  <label className="flex items-center cursor-pointer" data-id="element-9174">
                    <input type="checkbox" className="h-4 w-4 border-gray-300 rounded cursor-pointer" style={{
                  accentColor: brand.primary
                }} data-id="element-9175" />
                    <span className="ml-2 text-sm" style={{
                  color: colors.text.secondary
                }} data-id="element-9176">
                      Remember me
                    </span>
                  </label>
                  <button type="button" className="text-sm font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-9177">
                    Forgot password?
                  </button>
                </div>

                <button type="submit" className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: brand.primary,
              color: colors.text.inverse
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = brand.primaryHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = brand.primary} data-id="element-9178">
                  <LogInIcon size={18} className="mr-2" data-id="element-9179" />
                  Sign In
                </button>
              </form>

              <div className="mt-8 text-center lg:text-left" data-id="element-9180">
                <p className="text-sm" style={{
              color: colors.text.secondary
            }} data-id="element-9181">
                  Don't have a merchant account?{' '}
                  <button className="font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-9182">
                    Contact Sales
                  </button>
                </p>
              </div>
            </>}

          {step === 'mfa' && <>
              <button type="button" onClick={() => {
            setStep('login');
            setMfaCode(['', '', '', '', '', '']);
          }} className="flex items-center text-sm mb-8 transition-colors" style={{
            color: colors.text.tertiary
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[700]} onMouseLeave={e => e.currentTarget.style.color = colors.text.tertiary} data-id="element-9183">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-9184" />
                Back to sign in
              </button>

              <div className="text-center mb-8" data-id="element-9185">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
              backgroundColor: brand.primaryLight
            }} data-id="element-9186">
                  <MailIcon size={28} style={{
                color: brand.primary
              }} data-id="element-9187" />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-9188">
                  Check your email
                </h2>
                <p className="text-sm leading-relaxed" style={{
              color: colors.text.secondary
            }} data-id="element-9189">
                  We've sent a 6-digit verification code to
                </p>
                <p className="font-medium text-sm mt-1" style={{
              color: colors.text.primary
            }} data-id="element-9190">
                  {getMaskedEmail()}
                </p>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-6" data-id="element-9191">
                <div data-id="element-9192">
                  <label className="block text-sm font-medium mb-3 text-center" style={{
                color: colors.neutral[700]
              }} data-id="element-9193">
                    Enter verification code
                  </label>
                  <div className="flex justify-center gap-3" data-id="element-9194">
                    {mfaCode.map((digit, index) => <input key={index} ref={el => {
                  inputRefs.current[index] = el;
                }} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleMfaInput(index, e.target.value)} onKeyDown={e => handleMfaKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-semibold rounded-lg outline-none transition-all" style={{
                  border: `1px solid ${digit ? brand.primary : colors.border.DEFAULT}`,
                  backgroundColor: digit ? brand.primarySubtle : colors.neutral[50],
                  boxShadow: digit ? `0 0 0 1px ${brand.primaryRingActive}` : 'none'
                }} onFocus={e => {
                  e.target.select();
                  if (!digit) {
                    e.target.style.borderColor = brand.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                    e.target.style.backgroundColor = colors.neutral[0];
                  }
                }} onBlur={e => {
                  if (!digit) {
                    e.target.style.borderColor = colors.border.DEFAULT;
                    e.target.style.boxShadow = 'none';
                    e.target.style.backgroundColor = colors.neutral[50];
                  }
                }} data-id="element-9195" />)}
                  </div>
                </div>

                <button type="submit" disabled={!isCodeComplete} className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: isCodeComplete ? brand.primary : colors.neutral[100],
              color: isCodeComplete ? colors.text.inverse : colors.neutral[400],
              cursor: isCodeComplete ? 'pointer' : 'not-allowed'
            }} onMouseEnter={e => {
              if (isCodeComplete) e.currentTarget.style.backgroundColor = brand.primaryHover;
            }} onMouseLeave={e => {
              if (isCodeComplete) e.currentTarget.style.backgroundColor = brand.primary;
            }} data-id="element-9196">
                  <ShieldCheckIcon size={18} className="mr-2" data-id="element-9197" />
                  Verify & Sign In
                </button>
              </form>

              <div className="mt-6 text-center" data-id="element-9198">
                <p className="text-sm" style={{
              color: colors.text.tertiary
            }} data-id="element-9199">
                  Didn't receive the code?{' '}
                  {resendCountdown > 0 ? <span style={{
                color: colors.neutral[400]
              }} data-id="element-9200">
                      Resend in {resendCountdown}s
                    </span> : <button type="button" onClick={handleResend} className="font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-9201">
                      Resend code
                    </button>}
                </p>
              </div>

              <div className="mt-4 p-3.5 rounded-lg" style={{
            backgroundColor: colors.neutral[50],
            border: `1px solid ${colors.border.light}`
          }} data-id="element-9202">
                <div className="flex items-start gap-2.5" data-id="element-9203">
                  <ShieldCheckIcon size={16} className="mt-0.5 flex-shrink-0" style={{
                color: colors.neutral[400]
              }} data-id="element-9204" />
                  <p className="text-xs leading-relaxed" style={{
                color: colors.text.tertiary
              }} data-id="element-9205">
                    For your security, this code expires in 10 minutes. If you
                    didn't request this code, please secure your account
                    immediately.
                  </p>
                </div>
              </div>
            </>}

          <div className="mt-16 pt-6 text-center lg:text-left" style={{
          borderTop: `1px solid ${colors.border.light}`
        }} data-id="element-9206">
            <p className="text-xs" style={{
            color: colors.text.tertiary
          }} data-id="element-9207">
              © 2026 Rabbit Gateway. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>;
}